import { useEffect } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, CalendarCheck } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { SERVICES } from "../config/business";
import { useSectionNav } from "../hooks/useSectionNav";

export default function ServicesPage() {
  const go = useSectionNav();

  useEffect(() => {
    document.title = "Auto Repair Services | Auto Trust Centre — Mississauga";
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
    return () => {
      document.title = "Auto Repair Mississauga | Auto Trust Centre — Mechanic on Maingate Dr";
    };
  }, []);

  return (
    <>
      <Navbar />
      <main className="bg-[#0b0c0e]">
        <section className="spotlight relative overflow-hidden pb-16 pt-40 lg:pb-20 lg:pt-48">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.8 }}
              className="font-display flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-[#00AEEF] sm:text-sm"
            >
              <span className="inline-block h-[3px] w-10 bg-[#00AEEF]" />
              Complete Automotive Service
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="font-display mt-4 max-w-3xl text-4xl font-extrabold uppercase leading-[1.02] tracking-tight text-[#f4f4f2] sm:text-5xl lg:text-6xl"
            >
              Our <span className="text-[#00AEEF]">Services</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="mt-6 max-w-2xl text-base leading-relaxed text-[#c9ccd2] sm:text-lg"
            >
              From routine maintenance and diagnostics to major mechanical
              repairs and engine work, Auto Trust Centre provides complete
              automotive service under one roof — for all makes and models.
            </motion.p>
          </div>
        </section>

        <section className="pb-24">
          <div className="mx-auto grid max-w-7xl gap-5 px-5 md:grid-cols-2 lg:px-8">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 2) * 0.06} className="h-full">
                <article
                  data-testid={`services-page-card-${s.slug}`}
                  className={`flex h-full flex-col border bg-[#141519] p-7 ${
                    s.slug === "safety-certificate"
                      ? "border-[#00AEEF]/50"
                      : "border-white/10"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <s.icon className="h-8 w-8 shrink-0 text-[#00AEEF]" strokeWidth={1.6} />
                    <h2 className="font-display text-lg font-bold uppercase leading-snug tracking-wide text-[#f4f4f2]">
                      {s.title}
                    </h2>
                  </div>
                  {s.badge && (
                    <span className="mt-4 inline-flex w-fit items-center gap-1.5 border border-[#00AEEF]/50 bg-[#00AEEF]/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-[#00AEEF]">
                      <ShieldCheck className="h-3.5 w-3.5" />
                      {s.badge}
                    </span>
                  )}
                  <p className="mt-4 text-sm font-semibold text-[#e6e7ea]">
                    {s.blurb}
                  </p>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-[#a6a9b1]">
                    {s.detail}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-16">
            <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 border border-white/10 bg-[#141519] px-5 py-8 sm:px-8 lg:mx-auto lg:flex-row lg:items-center lg:p-12">
              <div>
                <h2 className="font-display text-2xl font-bold uppercase text-[#f4f4f2] sm:text-3xl">
                  Not Sure What Your Vehicle Needs?
                </h2>
                <p className="mt-2 max-w-xl text-[#a6a9b1]">
                  Tell us what's happening with your vehicle and we'll help
                  determine the next step.
                </p>
              </div>
              <button
                data-testid="services-page-book-button"
                onClick={() => go("appointment")}
                className="btn-primary shrink-0"
              >
                <CalendarCheck className="h-5 w-5" />
                Request an Appointment
              </button>
            </div>
          </Reveal>
        </section>
      </main>
      <Footer />
    </>
  );
}
